import React, { useRef, useEffect } from 'react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import type { ChatMessage as ChatMessageType } from '@/types';

interface ChatBotProps {
  messages: ChatMessageType[];
  isLoading: boolean;
  onSendMessage: (message: string) => void;
}

export const ChatBot: React.FC<ChatBotProps> = ({ messages, isLoading, onSendMessage }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Chat Assistant</h2>
      </div>
      <div style={styles.messages}>
        {messages.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>Start a conversation by asking a question about your data.</p>
            <p style={styles.exampleText}>
              Examples:
              <br />
              • "Show me a plot of charging sessions by location"
              <br />
              • "What is the average charging time?"
              <br />
              • "Create a Python script to analyze this dataset"
            </p>
          </div>
        ) : (
          messages.map((message, index) => (
            <ChatMessage key={index} message={message} />
          ))
        )}
        {isLoading && (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingDot}></div>
            <div style={styles.loadingDot}></div>
            <div style={styles.loadingDot}></div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <ChatInput onSend={onSendMessage} isLoading={isLoading} />
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    backgroundColor: 'white',
  },
  header: {
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e2e8f0',
  },
  title: {
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#0f172a',
  },
  messages: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '1.5rem',
  },
  emptyState: {
    textAlign: 'center' as const,
    color: '#64748b',
    marginTop: '2rem',
  },
  emptyText: {
    fontSize: '1rem',
    marginBottom: '1rem',
  },
  exampleText: {
    fontSize: '0.875rem',
    lineHeight: '1.75',
  },
  loadingContainer: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'center',
    padding: '1rem',
  },
  loadingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
};
